import React, { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { Link } from '@this/shared/ui/navigations/link';
import type { AITTheme } from '@this/shared/ui/theme';
import type ReserveInfo from '@this/domain/reserve_info2';
import TabiregiRegistrationModal from './tabiregi_registration_modal';
import TabiregiShareModal from './tabiregi_share_modal';

interface Props {
  reserveInfo: ReserveInfo;
}

const TabiregiSection: React.FC<Props> = ({ reserveInfo }) => {
  const theme = useTheme<AITTheme>();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [registered, setRegistered] = useState<boolean>(!!reserveInfo.tabiregiConsent?.consented);

  const handleOpenRegistrationModal = useCallback(() => {
    setIsRegistrationModalOpen(true);
  }, []);

  const handleCloseRegistrationModal = useCallback(() => {
    setIsRegistrationModalOpen(false);
  }, []);

  const handleOpenShareModal = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  const handleCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  const handleRegistrationCompleted = useCallback(() => {
    setRegistered(true);
  }, []);

  return (
    <div style={styles.wrapper} data-testid="tabiregi-section">
      <h3 style={styles.sectionTitle}>たびレジ</h3>
      <div style={styles.buttonArea}>
        <button
          type="button"
          style={
            registered ? { ...styles.tabiregiButton, opacity: 0.5, cursor: 'not-allowed' } : styles.tabiregiButton
          }
          onClick={handleOpenRegistrationModal}
          disabled={registered}
          data-testid="tabiregi-register-button"
        >
          {registered ? 'たびレジに登録済み' : 'たびレジに登録する'}
        </button>
        <button
          type="button"
          style={styles.tabiregiButton}
          onClick={handleOpenShareModal}
          data-testid="tabiregi-share-button"
        >
          同行者にたびレジ登録を教える
        </button>
      </div>
      <div style={styles.description}>
        <p style={styles.descriptionText}>
          ※外務省海外登録「たびレジ」について
          <br />
          たびレジとは、滞在先の最新の渡航情報や、現地に緊急事態が発生したときの連絡メール、いざというときの緊急連絡などを受け取ることができる外務省の無料サービスです。こちらで登録すれば、旅行日程、滞在先および連絡先が「たびレジ」に自動的に連携されるため、後で面倒な手続きが不要となります。また、「同行者に共有する」ボタンから日程表を共有することで、同行者の方も簡単にたびレジに登録することが可能です。
        </p>
        <p style={styles.descriptionText}>
          ※「たびレジ」の詳細な説明は下記よりご確認ください。
          <br />
          <Link href="https://www.ezairyu.mofa.go.jp/tabireg/index.html" target="_blank" isExternal>
            https://www.ezairyu.mofa.go.jp/tabireg/index.html
          </Link>
        </p>
        <p style={styles.descriptionText}>
          ※ たびレジに登録した情報は下記のリンクから登録解除可能です。
          <br />
          <Link href="https://www.ezairyu.mofa.go.jp/tabireg/simple/delete" target="_blank" isExternal>
            https://www.ezairyu.mofa.go.jp/tabireg/simple/delete
          </Link>
        </p>
      </div>
      {isRegistrationModalOpen && (
        <TabiregiRegistrationModal
          reserveInfo={reserveInfo}
          onClose={handleCloseRegistrationModal}
          onCompleted={handleRegistrationCompleted}
        />
      )}
      {isShareModalOpen && <TabiregiShareModal reserveInfo={reserveInfo} onClose={handleCloseShareModal} />}
    </div>
  );
};

function getStyles(theme: AITTheme) {
  return {
    wrapper: {
      marginBottom: '20px',
      padding: '15px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      background: '#fff'
    } as React.CSSProperties,
    sectionTitle: {
      fontSize: '16px',
      fontWeight: 'bold',
      marginBottom: '12px',
      paddingBottom: '8px',
      borderBottom: '1px solid #ddd'
    } as React.CSSProperties,
    buttonArea: {
      display: 'flex',
      gap: '10px',
      marginBottom: '15px',
      flexWrap: 'wrap'
    } as React.CSSProperties,
    tabiregiButton: {
      padding: '10px 20px',
      color: '#fff',
      backgroundColor: theme.linkColor || '#af985e',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold'
    } as React.CSSProperties,
    description: {
      fontSize: '12px',
      color: '#666',
      lineHeight: 1.6
    } as React.CSSProperties,
    descriptionText: {
      marginBottom: '8px'
    } as React.CSSProperties
  } as const;
}

export default TabiregiSection;
